/**
 * Auth API — typed wrappers for the identity/onboarding endpoints (email-
 * first OTP signup → create-organization, Google sign-in). Mirrors
 * public.api.ts's convention: page components call these, not api.service
 * directly. Credential-handling (storing tokens, redirecting) happens in
 * useAuth.ts, not here — this module is pure request/response.
 */
import api from './api.service';
import type { EffectivePermissions } from '@/store/slices/authSlice';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  // Optional: Google doesn't guarantee a family_name claim — see
  // google-auth.client.ts on the backend.
  lastName?: string;
  role: string;
  emailVerified?: boolean;
  /** @deprecated legacy flat module-key array, superseded by effectivePermissions. */
  permissions?: string[];
  effectivePermissions?: EffectivePermissions;
  tenantId?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthTenant {
  id: string;
  name: string;
  subdomain: string;
  planType?: string;
  modules?: Record<string, unknown>;
  features?: Record<string, unknown>;
}

/** Every identity/onboarding endpoint that issues a session returns this shape. */
export interface AuthResult {
  message: string;
  user: AuthUser;
  tenant?: AuthTenant | null;
  tokens: AuthTokens;
}

export interface StartSignupOptions {
  /** Cloudflare Turnstile token from TurnstileWidget — see
   *  turnstile.client.ts on the backend. Absent when Turnstile isn't
   *  configured (VITE_TURNSTILE_SITE_KEY unset), which the backend treats
   *  the same as "skip verification", not a failure. */
  turnstileToken?: string;
  /** Honeypot value — always sent empty by a real user (the field is
   *  visually hidden, never focused/filled by a human); a non-empty value
   *  here means a bot filled every input it could find. See Signup.tsx. */
  website?: string;
}

/** Signup step 1 — email only. Throws (409) if the email is already
 *  registered — the backend is deliberately explicit here, not silent, so
 *  the UI can nudge "sign in instead". */
export async function startSignup(email: string, options?: StartSignupOptions): Promise<{ message: string }> {
  const res = await api.post<{ success: boolean; data: { message: string } }>('/auth/signup/start', {
    email,
    turnstileToken: options?.turnstileToken,
    website: options?.website,
  });
  return res.data;
}

export interface DomainClaimWorkspace {
  tenantId: string;
  organizationName: string;
}

/** `workspaces` can have more than one entry — a company's email domain
 *  isn't guaranteed to map to exactly one tenant (see
 *  auth.service.ts#checkDomainClaim's doc comment on the backend). */
export type DomainClaimResult =
  | { claimed: false }
  | { claimed: true; workspaces: DomainClaimWorkspace[] };

/** Pre-flight check for the Slack-style "workspace already exists" UI
 *  branch — called BEFORE startSignup, so choosing "request to join" never
 *  costs an OTP send. Never blocks signup itself either way. */
export async function checkDomain(email: string): Promise<DomainClaimResult> {
  const res = await api.get<{ success: boolean; data: DomainClaimResult }>('/auth/check-domain', {
    params: { email },
  });
  return res.data;
}

/** "Request to join" — notifies the chosen workspace's admin. `tenantId`
 *  picks which one when checkDomain returned more than one for this
 *  domain — never re-guessed server-side. A separate, opt-in action; the
 *  normal signup flow ("create anyway") is unaffected either way. */
export async function requestToJoin(
  email: string,
  tenantId: string,
  name?: string
): Promise<{ message: string }> {
  const res = await api.post<{ success: boolean; data: { message: string } }>('/auth/request-to-join', {
    email,
    tenantId,
    name,
  });
  return res.data;
}

/** Signup step 2 — verify the OTP, get back a short-lived ticket that step 3
 *  redeems. Single-use: a second call with the same OTP fails even if it's
 *  still within its time window. */
export async function verifySignupOtp(email: string, otp: string): Promise<{ signupTicket: string }> {
  const res = await api.post<{ success: boolean; data: { message: string; signupTicket: string } }>(
    '/auth/signup/verify-otp',
    { email, otp }
  );
  return { signupTicket: res.data.signupTicket };
}

export interface CompleteSignupPayload {
  signupTicket: string;
  firstName: string;
  lastName: string;
  password: string;
  phoneNumber?: string;
}

/** Signup step 3 — redeem the ticket with name/password to actually create
 *  the (already-verified) identity. */
export async function completeSignup(payload: CompleteSignupPayload): Promise<AuthResult> {
  const res = await api.post<{ success: boolean; data: AuthResult }>('/auth/signup/complete', payload);
  return res.data;
}

/** Step 1 of Google sign-in. A known identity resolves straight to an
 *  AuthResult (isNewIdentity: false) — nothing new is being asserted about
 *  them, so there's nothing to confirm. A brand-new identity comes back as
 *  isNewIdentity: true with a preview of what Google gave us plus a
 *  short-lived ticket — NO account has been created yet. */
export type GooglePreviewResult =
  | ({ isNewIdentity: false } & AuthResult)
  | { isNewIdentity: true; googleTicket: string; preview: { email: string; firstName: string; lastName?: string } };

export async function googlePreview(idToken: string): Promise<GooglePreviewResult> {
  const res = await api.post<{ success: boolean; data: GooglePreviewResult }>('/auth/google', { idToken });
  return res.data;
}

export interface GoogleCompletePayload {
  googleTicket: string;
  firstName: string;
  lastName?: string | undefined;
}

/** Step 2 — only called once the user has reviewed and confirmed (or
 *  edited) the name on the confirm-your-details screen. This is the moment
 *  the account actually gets created. */
export async function googleComplete(payload: GoogleCompletePayload): Promise<AuthResult> {
  const res = await api.post<{ success: boolean; data: AuthResult }>('/auth/google/complete', payload);
  return res.data;
}

export interface CreateOrganizationPayload {
  organizationName: string;
  subdomain?: string;
  planType?: 'free' | 'pro' | 'enterprise';
}

/** Files a request — does NOT create a tenant. A super admin reviews it
 *  before any database is provisioned (see auth.service.ts#requestTenant on
 *  the backend). No tokens/tenant in the response since nothing changed
 *  about the user's session yet. */
export interface CreateOrganizationResult {
  requestId: string;
  status: 'pending';
}

export async function createOrganization(
  payload: CreateOrganizationPayload
): Promise<CreateOrganizationResult> {
  const res = await api.post<{ success: boolean; data: CreateOrganizationResult }>(
    '/auth/create-organization',
    payload
  );
  return res.data;
}

export interface OrganizationRequestStatus {
  status: 'pending' | 'approved' | 'rejected';
  organizationName: string;
  subdomain: string;
  rejectionReason?: string;
}

/** So a returning user (refresh, re-login) who already has a request in
 *  flight lands back on the pending screen instead of re-showing the form. */
export async function getOrganizationRequestStatus(): Promise<OrganizationRequestStatus | null> {
  const res = await api.get<{ success: boolean; data: { request: OrganizationRequestStatus | null } }>(
    '/auth/organization-request-status'
  );
  return res.data.request;
}


/** Forgot password — always resolves with the same generic reply whether or
 *  not the email has an account (the backend never reveals it). Rejects only
 *  on a malformed email (400) or the per-IP limit (429). */
export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email });
}

/** Completes a reset with the emailed token. 400 INVALID_TOKEN = link expired,
 *  already used, or superseded by a newer one. Success signs out every device. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await api.post('/auth/reset-password', { token, password });
}
